<template>
    <div class="mb-12 px-2.5">
        <div class="flex items-center justify-between mb-4">
            <h4 class="font-medium text-slate-800 dark:text-slate-100">Current period</h4>
            <!-- Mobile: Always show button, use modal for adding usage -->
            <template v-if="isMobile && !hasSubBenefits">
                <button
                    class="btn-base btn-transparent !text-slate-600 dark:!text-slate-300 !mt-0 !pr-0 disabled:!bg-white dark:disabled:!bg-slate-800"
                    :class="{
                        'opacity-50 cursor-not-allowed':
                            isAddDisabled,
                    }"
                    :disabled="isAddDisabled"
                    @click.stop="$emit('mobile-add-usage')"
                >
                    <Icon icon="plus" :width="16" :height="16" />
                    Add usage
                </button>
            </template>

            <!-- Desktop: Use tooltip for disabled state, popover for active state -->
            <template v-else-if="!hasSubBenefits">
                <UiTooltip
                    v-if="isBenefitFullyRedeemedThisYear"
                    text="You have fully utilized this benefit for the time period, and can not add anymore usage until the benefit resets."
                    placement="top"
                    :interactive="false"
                >
                    <template #trigger>
                        <span class="inline-block cursor-not-allowed">
                            <button
                                class="btn-base btn-transparent !text-slate-600 dark:!text-slate-300 !mt-0 !pr-0 opacity-50 !bg-white dark:!bg-slate-800 pointer-events-none"
                                disabled
                                tabindex="-1"
                            >
                                <Icon icon="plus" :width="16" :height="16" />
                                Add usage
                            </button>
                        </span>
                    </template>
                </UiTooltip>
                <button
                    v-else-if="isAddDisabled"
                    class="btn-base btn-transparent !text-slate-600 dark:!text-slate-300 !mt-0 !pr-0 opacity-50 cursor-not-allowed !bg-white dark:!bg-slate-800"
                    disabled
                >
                    <Icon icon="plus" :width="16" :height="16" />
                    Add usage
                </button>
                <ButtonPopover
                    v-else
                    ref="popoverRef"
                    @click.stop="$emit('popover-opened', 'current')"
                    buttonClass="btn-base btn-transparent !text-slate-600 dark:!text-slate-300 !mt-0 !pr-0"
                    popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-80 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700"
                >
                    <template #button-content>
                        <span
                            class="inline-flex items-center gap-2"
                            @click="addUsageMode = 'menu'"
                        >
                            <Icon icon="plus" :width="16" :height="16" />
                            Add usage
                        </span>
                    </template>
                    <template #default="{ closePopover }">
                        <!-- MY1-3080: an auto-tracked benefit first chooses between a
                             My Spending transaction and manual entry -->
                        <AddUsageMenu
                            v-if="showSpendingOption && addUsageMode === 'menu'"
                            @spending="
                                closePopover();
                                $emit('add-from-spending');
                            "
                            @manual="addUsageMode = 'manual'"
                        />
                        <div v-else class="p-4">
                            <BenefitsUsageForm
                                v-model="localFormData"
                                :hideLabels="true"
                                :errors="formErrors"
                                :isCountBased="isCountBased"
                                :countLabel="countLabel"
                            />
                            <div
                                class="flex justify-between items-center mt-4"
                            >
                                <!-- Remaining NCRED Display -->
                                <div
                                    class="text-slate-500 dark:text-slate-400 text-sm"
                                    v-if="isNcred && parseFloat(remainingAmount) > 0"
                                >
                                    {{ parseInt(remainingAmount) }} {{ ncredUnitLabel(remainingAmount) }} remaining
                                </div>
                                <!-- Remaining Count Display -->
                                <div
                                    class="text-slate-500 dark:text-slate-400 text-sm"
                                    v-else-if="(isCountBased || isByCountValueBenefit) && remainingCount > 0"
                                >
                                    {{ remainingCount }} remaining
                                </div>
                                <!-- Remaining Amount Display -->
                                <div
                                    class="text-slate-500 dark:text-slate-400 text-sm"
                                    v-else-if="!isCountBased && !isByCountValueBenefit && parseFloat(remainingAmount) > 0"
                                >
                                    {{ filters.formatSmartMoney(remainingAmount) }}
                                    remaining
                                </div>
                                <!-- Fully redeemed display -->
                                <div
                                    v-else
                                    class="text-green-600 text-sm font-medium"
                                >
                                    Fully redeemed!
                                </div>

                                <!-- Submit Button -->
                                <button
                                    @click="$emit('usage-submit', closePopover)"
                                    class="btn-base btn-dark"
                                    :disabled="isNcred ? parseFloat(remainingAmount) <= 0 : ((isCountBased || isByCountValueBenefit) ? remainingCount <= 0 : !parseFloat(remainingAmount))"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Redeem</span>
                                </button>
                            </div>
                        </div>
                    </template>
                </ButtonPopover>
            </template>
        </div>

        <template v-if="!showIneligibleMessage">
            <!-- Compound benefit: aggregate usage line -->
            <template v-if="hasSubBenefits">
                <div class="text-sm text-slate-800 dark:text-slate-100 mb-2">
                    {{ filters.formatSmartMoney(aggregateSubUsage) }}
                    of
                    {{ filters.formatSmartMoney(displayAmount) }}
                </div>
                <!-- Aggregate Progress Bar -->
                <div class="w-full bg-orange-200 rounded-full h-2 mb-4 relative">
                    <div
                        class="bg-10x-orange h-2 rounded-full transition-all duration-300"
                        :style="{ width: `${Math.min(aggregateUtilization, 100)}%` }"
                    ></div>
                </div>
                <!-- Individual sub-benefit rows -->
                <div class="space-y-3 mb-2">
                    <div
                        v-for="sub in selectedBenefit.sub_benefits"
                        :key="sub.id"
                        class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3"
                    >
                        <div class="flex items-center justify-between mb-1.5">
                            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ sub.benefit_title }}</span>
                            <ButtonPopover
                                buttonClass="btn-base btn-transparent !text-slate-600 dark:!text-slate-300 !mt-0 !p-1 !text-xs"
                                popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-64 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700"
                            >
                                <template #button-content>
                                    <Icon icon="plus" :width="12" :height="12" />
                                    Add usage
                                </template>
                                <template #default="{ closePopover }">
                                    <div class="p-4">
                                        <div class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-3">{{ sub.benefit_title }}</div>
                                        <BenefitsUsageForm
                                            v-model="subForms[sub.id]"
                                            :hideLabels="true"
                                            :errors="subFormErrors[sub.id] || {}"
                                            :isCountBased="sub.values_summary?.is_count_based"
                                            :countLabel="sub.value_details?.count_label || ''"
                                        />
                                        <div class="flex justify-between items-center mt-4">
                                            <div class="text-slate-500 dark:text-slate-400 text-xs" v-if="getSubRemaining(sub) !== null">
                                                {{ getSubRemaining(sub) }} remaining
                                            </div>
                                            <span v-else class="text-red-500 text-xs">
                                                {{ sub.values_summary?.is_count_based ? 'No remaining uses!' : 'No remaining value!' }}
                                            </span>
                                            <button
                                                :disabled="subRedeemDisabled(sub)"
                                                @click="submitSubBenefit(sub, closePopover)"
                                                class="bg-slate-800 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-slate-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-800"
                                            >
                                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Redeem
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </ButtonPopover>
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400 mb-1.5">
                            <template v-if="sub.values_summary?.is_count_based">
                                {{ subCreditsUsed(sub) }} of {{ sub.value_details?.count || 0 }} {{ sub.value_details?.count_label || 'credits' }} used
                            </template>
                            <template v-else>
                                {{ filters.formatSmartMoney(sub.current_period_usage) }} of {{ filters.formatSmartMoney(sub.amount) }}
                            </template>
                        </div>
                        <div class="w-full bg-orange-200 rounded-full h-1">
                            <div
                                class="bg-10x-orange h-1 rounded-full transition-all"
                                :style="{ width: `${subUsagePercent(sub)}%` }"
                            ></div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Regular (non-compound) benefit usage display -->
            <template v-else>
                <div v-if="isNcred" class="text-sm text-slate-800 dark:text-slate-100 mb-2">
                    {{ currentPeriodUsage }}
                    of
                    {{ displayAmount }}
                    {{ ncredUnitLabel(displayAmount) }}
                </div>
                <div v-else-if="!isCountBased" class="text-sm text-slate-800 dark:text-slate-100 mb-2">
                    {{ filters.formatSmartMoney(currentPeriodUsage) }}
                    of
                    {{ filters.formatSmartMoney(displayAmount) }}
                </div>
                <div v-else class="text-sm text-slate-800 dark:text-slate-100 mb-2">
                    {{ currentPeriodUsage }}
                    of
                    {{ selectedBenefit?.benefit?.value_details?.count || 0 }}
                    {{ selectedBenefit?.benefit?.value_details?.count_label }}
                </div>

                <!-- Progress Bar -->
                <div class="w-full bg-orange-200 rounded-full h-2 mb-2 relative">
                    <div
                        class="bg-10x-orange h-2 rounded-full transition-all duration-300"
                        :style="{ width: `${Math.min(utilizationPercentage, 100)}%` }"
                    ></div>
                    <!-- 100% Completion Indicator -->
                    <div
                        v-if="showFullyRedeemedIcon"
                        class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                    >
                        <Icon icon="check" svgClass="w-2 h-2 text-white" class="text-white" />
                    </div>
                </div>
            </template>
        </template>

        <div
            v-if="expirationTime && selectedBenefit?.benefit?.period_type && selectedBenefit?.is_active !== false"
            class="flex justify-between items-center gap-3 text-sm text-slate-500 dark:text-slate-400"
        >
            <span class="group flex gap-1">
                <span v-if="!showIneligibleMessage">
                    {{ expirationTime === 'Today' ? 'Expires today' : `Expires in ${expirationTime}` }}
                </span>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">
                    Ineligible until {{ formattedPeriodEndDate }}
                </div>
                <div class="opacity-0 group-hover:opacity-100 transition-opacity items-center gap-1 flex">
                    <button
                        @click.stop="$emit('edit-renewal-date')"
                        class="p-1 hover:bg-slate-200 rounded"
                        title="Enter custom renewal date"
                    >
                        <Icon icon="pencil" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>
            </span>

            <UiTooltip :text="renewalTooltipText" placement="top" :interactive="false">
                <template #trigger>
                    <span class="flex items-center gap-1 cursor-help">
                        <Icon icon="refresh" :width="16" :height="16" />
                        {{ formattedRenewalLabel }}
                    </span>
                </template>
            </UiTooltip>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useFormDataSync } from '@/composables/useFormDataSync.js';
import { useCardBenefitsStore } from '@/store/card-benefits';
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import BenefitsUsageForm from '@/components/card-benefits/benefits-usage-form.vue';
import AddUsageMenu from '@/components/card-benefits/add-usage-menu.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import filters from '@/filters';
import dayjs from 'dayjs';

const props = defineProps({
    selectedBenefit: { type: Object, default: null },
    isCountBased: { type: Boolean, required: true },
    isByCountValueBenefit: { type: Boolean, default: false },
    isMobile: { type: Boolean, required: true },
    displayAmount: { type: Number, default: 0 },
    currentPeriodUsage: { type: Number, default: 0 },
    utilizationPercentage: { type: Number, default: 0 },
    showFullyRedeemedIcon: { type: Boolean, default: false },
    showIneligibleMessage: { type: Boolean, default: false },
    isAddDisabled: { type: Boolean, default: false },
    // MY1-3080: offer "Add from My Spending" (auto-tracked benefits only).
    showSpendingOption: { type: Boolean, default: false },
    isBenefitFullyRedeemedThisYear: { type: Boolean, default: false },
    expirationTime: { type: String, default: null },
    formattedPeriodEndDate: { type: String, default: '' },
    renewalTooltipText: { type: String, default: '' },
    formattedRenewalLabel: { type: String, default: '' },
    remainingAmount: { type: [String, Number], default: '0' },
    remainingCount: { type: Number, default: 0 },
    countLabel: { type: String, default: '' },
    formErrors: { type: Object, default: () => ({}) },
    usageFormData: { type: Object, required: true },
});

const emit = defineEmits([
    'mobile-add-usage',
    'add-from-spending',
    'popover-opened',
    'usage-submit',
    'edit-renewal-date',
    'update:usageFormData',
]);

// MY1-3080: 'menu' (choose source) or 'manual' (the existing form).
const addUsageMode = ref('menu');

const store = useCardBenefitsStore();
const popoverRef = ref(null);

const { localFormData } = useFormDataSync(() => props.usageFormData, emit);

// Sub-benefit support
const hasSubBenefits = computed(() => (props.selectedBenefit?.sub_benefits?.length ?? 0) > 0);

const aggregateSubUsage = computed(() => {
    if (!hasSubBenefits.value) {
        return 0;
    }
    return (props.selectedBenefit.sub_benefits || []).reduce(
        (sum, s) => sum + (parseFloat(s.current_period_usage) || 0),
        0
    );
});

const aggregateUtilization = computed(() => {
    const max = parseFloat(props.displayAmount || 0);
    return max > 0 ? Math.round((aggregateSubUsage.value / max) * 100) : 0;
});

const subCreditsUsed = (sub) => {
    const perUnit = parseFloat(sub.amount || 0);
    if (sub.values_summary?.is_count_based && perUnit > 0) {
        return Math.floor(parseFloat(sub.current_period_usage || 0) / perUnit);
    }
    return parseFloat(sub.current_period_usage || 0);
};

const subUsagePercent = (sub) => {
    const max = sub.values_summary?.is_count_based
        ? parseFloat(sub.value_details?.count || 0)
        : parseFloat(sub.amount || 0);
    const used = sub.values_summary?.is_count_based ? subCreditsUsed(sub) : parseFloat(sub.current_period_usage || 0);
    return max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
};

// Per-sub-benefit form state
const defaultForm = () => ({ amount: 0, date: dayjs().format('YYYY-MM-DD'), count: 0 });
const subForms = ref({});
const subFormErrors = ref({});

watch(
    () => props.selectedBenefit?.sub_benefits,
    (subs) => {
        if (!subs) {
            return;
        }
        subs.forEach((s) => {
            if (!subForms.value[s.id]) {
                subForms.value[s.id] = defaultForm();
            }
        });
    },
    { immediate: true, deep: false }
);

// Returns null only when the sub-benefit is *actually* exhausted (used >= total).
// When the user types a value that would push remaining to 0 (a valid redemption
// of the last credit), we still return the preview string ("0", "$0.00") so the
// Redeem button stays enabled. The button-disabled state is computed separately
// in subRedeemDisabled() against the un-previewed remaining.
const getSubRemaining = (sub) => {
    const formData = subForms.value[sub.id] || {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        if (total - used <= 0) return null;
        const remaining = Math.max(0, total - used - parseFloat(formData.count || 0));
        return `${remaining}`;
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    if (total - used <= 0) return null;
    const remaining = Math.max(0, total - used - parseFloat(formData.amount || 0));
    return filters.formatSmartMoney(remaining);
};

// Disable the Redeem button only when the user *cannot* validly submit:
// either the sub-benefit is already exhausted, or they typed more than what
// is actually remaining. Typing the exact remaining amount stays valid.
const subRedeemDisabled = (sub) => {
    const formData = subForms.value[sub.id] || {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        const total = parseFloat(sub.value_details?.count || 0);
        const used = subCreditsUsed(sub);
        const typed = parseFloat(formData.count || 0);
        return total - used <= 0 || typed > total - used;
    }
    const total = parseFloat(sub.amount || 0);
    const used = parseFloat(sub.current_period_usage || 0);
    const typed = parseFloat(formData.amount || 0);
    return total - used <= 0 || typed > total - used;
};

const submitSubBenefit = async (sub, closePopover) => {
    const formData = subForms.value[sub.id] || {};
    subFormErrors.value[sub.id] = {};
    const isCountBased = sub.values_summary?.is_count_based;
    if (isCountBased) {
        if (!formData.count) {
            subFormErrors.value[sub.id] = { usageCount: 'Enter count' };
            return;
        }
    } else {
        if (!formData.amount) {
            subFormErrors.value[sub.id] = { usageAmount: 'Enter amount' };
            return;
        }
    }

    const success = await store.handleSubBenefitUsageSubmit(
        formData,
        sub,
        props.selectedBenefit?.connected_account_id
    );

    if (success) {
        subForms.value[sub.id] = defaultForm();
        closePopover();
    }
};

const isNcred = computed(
    () => props.selectedBenefit?.benefit?.values_summary?.currency === 'NCRED'
);

const ncredUnitLabel = (value) => {
    const label =
        props.selectedBenefit?.benefit?.values_summary?.unit_label ||
        'night credits';
    if (parseFloat(value) === 1 && label.endsWith('s')) {
        return label.slice(0, -1);
    }
    return label;
};

defineExpose({ popoverRef });
</script>
